import { useState, useCallback, useEffect } from 'react';
import { IWidget, WidgetView } from 'common';
import { createChangeSettings } from '../utils/createChangeSettings';
import { QuickLayout } from '../utils/quickSettingsUtils';

interface UseQuickProductsProps {
  editedWidget: {
    name: string;
    description: string;
    settings: IWidget['settings'];
  };
  setEditedWidget: (widget: any) => void;
  currentLayout: QuickLayout;
  carouselType?: IWidget['settings']['carousel_type'];
}

// Template definitions for "not fine-tuned" states (excluding STL)
const createTemplate = (exactEnabled: boolean, similarEnabled: boolean) => {
  return {
    has_exact_products: exactEnabled,
    has_similar_products: similarEnabled,
    min_exact_products: 1,
    min_similar_products: 1,
    display_exact_products_only: exactEnabled && !similarEnabled,
    display_similar_products_only: similarEnabled && !exactEnabled,
    max_exact_products_displayed: 6,
    max_similar_products_displayed: 6,
  };
};

// Helper to create STL settings based on product state and layout
const createSTLSettings = (
  exactEnabled: boolean,
  similarEnabled: boolean,
  layout: QuickLayout,
  carouselType?: IWidget['settings']['carousel_type'],
) => {
  const hasAnyProducts = exactEnabled || similarEnabled;
  const carouselDisplay =
    carouselType === 'uniform' ? ('modal' as WidgetView['settings']['shop_this_look_display']) : ('product_list' as WidgetView['settings']['shop_this_look_display']);
  return {
    shop_this_look: hasAnyProducts,
    shop_this_look_display: hasAnyProducts
      ? layout === 'carousel'
        ? carouselDisplay
        : ('modal' as WidgetView['settings']['shop_this_look_display'])
      : ('none' as WidgetView['settings']['shop_this_look_display']),
  };
};

// All valid "not fine-tuned" templates
const getValidTemplates = () => [
  createTemplate(true, true), // Both enabled
  createTemplate(true, false), // Only exact
  createTemplate(false, true), // Only similar
  createTemplate(false, false), // Both disabled
];

export const useQuickProducts = ({
  editedWidget,
  setEditedWidget,
  currentLayout,
  carouselType,
}: UseQuickProductsProps) => {
  const changeSettings = createChangeSettings(editedWidget, setEditedWidget);

  // State for checkbox values - initialize based on actual widget settings
  const [isExactEnabled, setIsExactEnabled] = useState<boolean>(
    editedWidget.settings.has_exact_products || false,
  );
  const [isSimilarEnabled, setIsSimilarEnabled] = useState<boolean>(
    editedWidget.settings.has_similar_products || false,
  );

  // Check if current settings match any valid template
  const checkIsFineTuned = useCallback((): boolean => {
    const validTemplates = getValidTemplates();

    // Extract only the relevant settings from current widget (excluding STL)
    const currentSettings = {
      has_exact_products: editedWidget.settings.has_exact_products,
      has_similar_products: editedWidget.settings.has_similar_products,
      min_exact_products: editedWidget.settings.min_exact_products,
      min_similar_products: editedWidget.settings.min_similar_products,
      display_exact_products_only: editedWidget.settings.display_exact_products_only,
      display_similar_products_only: editedWidget.settings.display_similar_products_only,
      max_exact_products_displayed: editedWidget.settings.max_exact_products_displayed,
      max_similar_products_displayed: editedWidget.settings.max_similar_products_displayed,
    };

    // Check if current settings match any template
    const isFineTuned = !validTemplates.some((template) => {
      return Object.keys(template).every((key) => {
        const currentValue = currentSettings[key as keyof typeof currentSettings];
        const templateValue = template[key as keyof typeof template];

        // If this product type is disabled, ignore its min value
        if (key === 'min_exact_products' && !currentSettings.has_exact_products) {
          return true;
        }
        if (key === 'min_similar_products' && !currentSettings.has_similar_products) {
          return true;
        }

        return currentValue === templateValue;
      });
    });

    return isFineTuned;
  }, [editedWidget.settings, currentLayout]);

  const [isFineTuned, setIsFineTuned] = useState<boolean>(false);

  // Update fine-tuned state when settings or layout change
  useEffect(() => {
    const newFineTuned = checkIsFineTuned();
    setIsFineTuned(newFineTuned);
  }, [editedWidget.settings, currentLayout, checkIsFineTuned]);

  // Update checkbox state when settings change externally (from advanced settings)
  useEffect(() => {
    // Try to adapt advanced settings to quick settings
    const adaptedState = adaptAdvancedSettingsToQuickSettings();

    setIsExactEnabled(adaptedState.exactEnabled);
    setIsSimilarEnabled(adaptedState.similarEnabled);
  }, [
    editedWidget.settings.has_exact_products,
    editedWidget.settings.has_similar_products,
    editedWidget.settings.min_exact_products,
    editedWidget.settings.min_similar_products,
    editedWidget.settings.display_exact_products_only,
    editedWidget.settings.display_similar_products_only,
  ]);

  // Function to adapt advanced settings to quick settings when possible
  const adaptAdvancedSettingsToQuickSettings = useCallback(() => {
    const settings = editedWidget.settings;

    // Start with the basic enabled state
    let exactEnabled = settings.has_exact_products || false;
    let similarEnabled = settings.has_similar_products || false;

    // Handle special cases from advanced settings

    // Case 1: If display_only flags suggest a different state
    if (settings.display_exact_products_only && settings.display_similar_products_only) {
      // Invalid state - both can't be "only", default to both enabled
      exactEnabled = true;
      similarEnabled = true;
    } else if (settings.display_exact_products_only) {
      // Only exact should be enabled
      exactEnabled = true;
      similarEnabled = false;
    } else if (settings.display_similar_products_only) {
      // Only similar should be enabled
      exactEnabled = false;
      similarEnabled = true;
    }

    // Case 2: If STL is 'none', disable both products
    if (settings.shop_this_look_display === 'none') {
      exactEnabled = false;
      similarEnabled = false;
    }

    return { exactEnabled, similarEnabled };
  }, [editedWidget.settings]);

  // Toggle exact products
  const toggleExactProducts = useCallback(() => {
    const newExactEnabled = !isExactEnabled;
    setIsExactEnabled(newExactEnabled);

    // Apply the template for this combination
    const productSettings = createTemplate(newExactEnabled, isSimilarEnabled);
    const stlSettings = createSTLSettings(
      newExactEnabled,
      isSimilarEnabled,
      currentLayout,
      carouselType,
    );
    const newSettings = { ...productSettings, ...stlSettings };
    changeSettings(newSettings);
  }, [isExactEnabled, isSimilarEnabled, currentLayout, carouselType, changeSettings]);

  // Toggle similar products
  const toggleSimilarProducts = useCallback(() => {
    const newSimilarEnabled = !isSimilarEnabled;
    setIsSimilarEnabled(newSimilarEnabled);

    // Apply the template for this combination
    const productSettings = createTemplate(isExactEnabled, newSimilarEnabled);
    const stlSettings = createSTLSettings(
      isExactEnabled,
      newSimilarEnabled,
      currentLayout,
      carouselType,
    );
    const newSettings = { ...productSettings, ...stlSettings };
    changeSettings(newSettings);
  }, [isExactEnabled, isSimilarEnabled, currentLayout, carouselType, changeSettings]);

  // Reset to default template (both enabled)
  const resetFineTunedSettings = useCallback(() => {
    setIsExactEnabled(true);
    setIsSimilarEnabled(true);

    const productSettings = createTemplate(true, true);
    const stlSettings = createSTLSettings(true, true, currentLayout, carouselType);
    const newSettings = { ...productSettings, ...stlSettings };
    changeSettings(newSettings);
  }, [currentLayout, changeSettings]);

  return {
    isExactEnabled,
    isSimilarEnabled,
    isFineTuned,
    toggleExactProducts,
    toggleSimilarProducts,
    resetFineTunedSettings,
  };
};
